import { createTheme, type Theme } from '@mui/material/styles';
import { adminPalette, makePalette, palette, publicPalette } from './palette';
import { typography } from './typography';
import { makeComponents } from './components';

/**
 * The portal's own palette, carried on the theme.
 *
 * MUI's `theme.palette` has room for a primary ramp and the intent slots, and
 * the design system needs more than that — the accent tint, the muted stroke,
 * the rail. Shared components in this library render inside all three portals,
 * so they read the accent from here rather than importing the module-level
 * `palette`, which is the Government Portal's and would leave a teal chat
 * bubble in a petrol-blue admin.
 */
declare module '@mui/material/styles' {
  interface Theme {
    portal: ReturnType<typeof makePalette>;
  }
  interface ThemeOptions {
    portal?: ReturnType<typeof makePalette>;
  }
}

/**
 * Build a portal theme from one accent family.
 *
 * Shape, type, motion and every semantic colour are identical across the
 * portals by design — what a theme carries is the accent, and the chrome
 * derived from it. The three portals used to be pixel-identical (`public`
 * rendered `govTheme` verbatim, and `adminTheme` only overrode a primary the
 * sidenav never read), so nothing but the URL told an operator which one they
 * were looking at.
 */
export function makePortalTheme(portalPalette: ReturnType<typeof makePalette>): Theme {
  return createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: portalPalette.brand.accent,
        light: portalPalette.brand.accentLight,
        dark: portalPalette.brand.rail,
        contrastText: portalPalette.text.onDark,
      },
      error: { main: portalPalette.semantic.emergency },
      warning: { main: portalPalette.semantic.warning },
      info: { main: portalPalette.semantic.pending },
      success: { main: portalPalette.semantic.success },
      background: {
        default: portalPalette.background.default,
        paper: portalPalette.background.paper,
      },
      text: {
        primary: portalPalette.text.primary,
        secondary: portalPalette.text.secondary,
        disabled: portalPalette.text.muted,
      },
      divider: portalPalette.border.onCream,
    },
    shape: { borderRadius: 8 },
    typography,
    transitions: {
      duration: {
        shortest: 150,
        shorter: 200,
        short: 200,
        standard: 200,
        complex: 250,
        enteringScreen: 200,
        leavingScreen: 200,
      },
      easing: {
        easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
        easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
        easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
        sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
      },
    },
    components: makeComponents(portalPalette),
    portal: portalPalette,
  });
}

/** Government Portal — brand teal. */
export const govTheme = makePortalTheme(palette);

/** Client Portal — indigo. */
export const publicTheme = makePortalTheme(publicPalette);

/** Admin portal — petrol blue. */
export const adminTheme = makePortalTheme(adminPalette);

export type GovTheme = typeof govTheme;
